/**
 * A past ticket's conversation as the viewers show it, fetched by ticket id through the thread API,
 * with the lines that sum it up: who wrote, how long the thread is and when it was resolved.
 */

import { useTicketThread } from '@/hooks';
import { threadToTranscript, type TicketThread } from '@/services/ticket.service';

const resolvedLabel = (thread: TicketThread): string | null =>
  thread.resolvedAt
    ? new Date(thread.resolvedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

/** Helpdesks store "Unknown" for an anonymous writer; that is no name to show. */
export const customerLabel = (thread: TicketThread): string | null => {
  const name = thread.customer?.name || thread.customer?.email || null;
  return name && name.trim().toLowerCase() !== 'unknown' ? name : null;
};

export const metaLine = (thread: TicketThread, count: number): string =>
  [
    `${count} message${count === 1 ? '' : 's'}`,
    resolvedLabel(thread) ? `resolved ${resolvedLabel(thread)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

/** A ticket's conversation as the viewers show it, with the line that sums it up once it has loaded. */
export const useTicketTranscript = (ticketId: string | null) => {
  const { data: thread, isLoading, isError } = useTicketThread(ticketId);
  const transcript = thread ? threadToTranscript(thread) : [];
  const summary = thread
    ? [customerLabel(thread), metaLine(thread, transcript.length)].filter(Boolean).join(' · ')
    : null;
  return { thread, transcript, summary, isLoading, isError };
};

export type TicketTranscript = ReturnType<typeof useTicketTranscript>;
